import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, orgchart } from '@cwos/api-client';
import type { users as UsersNS } from '@cwos/api-client';
import { Button, ConsequenceNote, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, authorityGateAll, gateOpen, gateSentence } from '../authorityGate.js';
import { writeRefusalMessage, refusedForReservedKey } from '../ceilingRefusal.js';
import { listPhase } from '../listState.js';
import { pickerState } from '../pickerState.js';
import { personDirectory } from '../personDirectory.js';
import { LoadFailure } from '../LoadFailure.js';
import {
  grantStandingBadge,
  keyStandings,
  standingCensus,
  type KeyStanding,
} from './grantability.js';

/**
 * COMPOSED ROLES — where over-granting hides, and where a reserved key would be
 * laundered if the screen let it.
 *
 * ===========================================================================
 * 1 · THE SCREEN SHOWS EFFECTIVE AUTHORITY, AND ONLY THE SERVER'S
 * ===========================================================================
 * An administrator ticking three reasonable-looking permissions can create
 * something they never intended. The honest answer to "what does this person
 * end up with" is not the union of the boxes they ticked — scope narrows some
 * grants, the privilege ceiling refuses others outright, and a composed role is
 * MATERIALISED into the holder's grants at assignment time by the server.
 *
 * So the effective-authority panel on this screen is a read of
 * `GET /orgchart/users/:id/effective-authority`, taken AFTER the assignment,
 * and it is the only thing here that claims to describe somebody's authority.
 * The tick list describes a REQUEST. The two are labelled as different things
 * on purpose, and the request is never presented as the outcome.
 *
 * ===========================================================================
 * 2 · THE COMPOSITION OF AN EXISTING ROLE, AND THE THREE ANSWERS IT HAS
 * ===========================================================================
 * `composed_role_permission` used to be written by `POST /orgchart/roles` and
 * `PUT /orgchart/roles/:key`, read by `assignComposedRole` inside the service,
 * and served by NO route — while `GET /roles/overview` built `permissionKeys`
 * by joining `role_permission`, a table composition never writes. The overview
 * therefore reported every composed role as conferring ZERO permissions, and an
 * administrator was replacing, through a route that REPLACES, a set they could
 * not see.
 *
 * `/roles/overview` now sends `composedPermissionKeys` and `composedPermissions`
 * on every non-system role, and this surface renders whichever of THREE answers
 * the server gave — never one standing in for another:
 *
 *   `undefined`  the server did not say (a bundle newer than its API). Still
 *                UNREADABLE, exactly as this screen used to report always.
 *   `[]`         composed and confers nothing. A measured zero.
 *   `[...]`      what it confers, with each key's scope.
 *
 * Nothing here is a client-side memory of what was last submitted from this
 * browser. A recomposition still states plainly that it REPLACES the set,
 * because `recomposeRole` clears the entries and rewrites them.
 *
 * ===========================================================================
 * 3 · RESERVED KEYS ARE SHOWN AS RESERVED
 * ===========================================================================
 * `permissionGrantRefusal` refuses a reserved key on this door as well as the
 * direct-grant door — the orgchart doors leave `grantabilityGate` at `'apply'`
 * precisely because their keys are caller-supplied. A picker that hid reserved
 * keys would make that refusal look like a bug when the key turns up in an
 * audit row; a picker that showed them as ordinary unticked boxes would invite
 * an administrator to keep trying. Both failures are avoided the same way: the
 * key is listed, it is not selectable, it carries a `Reserved` badge, and the
 * server's own `reservedReason` sits under it.
 *
 * See `grantability.ts` — the standing is the SERVER'S field, arriving on
 * `GET /roles/overview`'s `metadata`, never a list kept on this side.
 */
export function ComposedRoles({ overview }: { overview: UsersNS.RolesOverview }) {
  const permissions = usePermissions();
  const qc = useQueryClient();

  const composeGate = authorityGate(permissions, 'role:compose');
  // The assign door charges BOTH keys — the route checks `role:compose` and
  // then `role:assign` separately, and a reader holding one gets a 403 naming
  // the other. Gating on either alone offers a control that always refuses.
  const assignGate = authorityGateAll(permissions, ['role:compose', 'role:assign']);

  const standings = useMemo(() => keyStandings(overview), [overview]);
  const census = useMemo(() => standingCensus(standings), [standings]);
  const byModule = useMemo(
    () => groupStandings(standings, overview.modules),
    [standings, overview],
  );

  const [roleKey, setRoleKey] = useState('');
  const [roleName, setRoleName] = useState('');
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composed, setComposed] = useState<orgchart.ComposedRoleView | null>(null);
  /**
   * WHICH EXISTING ROLE THE TICKED SET WOULD REPLACE, or `''` for a new one.
   *
   * `recomposeRole` CLEARS `composed_role_permission` and rewrites it, then
   * re-materialises the result for every current holder in the same
   * transaction. So this is a replacement, not an addition — and because no
   * route serves the current set, an administrator cannot see what they are
   * replacing. Both facts are stated beside the control rather than discovered
   * afterwards.
   */
  const [replacing, setReplacing] = useState('');

  const compose = useMutation({
    mutationFn: () =>
      orgchart.composeRole({
        key: roleKey.trim(),
        name: roleName.trim(),
        // Org scope only, from this surface. A program- or location-confined
        // entry needs a scope picker that can name a real program or location,
        // and offering the field without it would produce a role whose scope is
        // a uuid the administrator typed. The server accepts both; this screen
        // composes the one it can state honestly.
        permissions: picked.map((key) => ({ key, scopeType: 'org' as const, scopeId: null })),
      }),
    onSuccess: (role) => {
      setComposed(role);
      setComposeError(null);
      void qc.invalidateQueries({ queryKey: ['roles', 'overview'] });
    },
    onError: (err: unknown) =>
      setComposeError(writeRefusalMessage(err, 'We couldn’t compose that role.')),
  });

  const recompose = useMutation({
    mutationFn: () =>
      orgchart.recomposeRole(
        replacing,
        picked.map((key) => ({ key, scopeType: 'org' as const, scopeId: null })),
      ),
    onSuccess: (role) => {
      setComposed(role);
      setComposeError(null);
      void qc.invalidateQueries({ queryKey: ['roles', 'overview'] });
    },
    onError: (err: unknown) =>
      setComposeError(writeRefusalMessage(err, 'We couldn’t recompose that role.')),
  });

  const composedRoles = overview.roles.filter((r) => !r.isSystem);
  const mayCompose = gateOpen(composeGate);
  const composeNote = gateSentence(composeGate, 'compose roles in this organization');

  return (
    <section className="rn-card rn-col" aria-label="Composed roles">
      <h2 className="rn-card-title">Composed roles</h2>
      <p className="rn-muted">
        A composed role is a set of permissions your organization defines and hands to people. It is
        the highest-leverage control in this product: three reasonable-looking permissions can add
        up to something nobody intended, so what a person ends up holding is read back from the
        server after the fact rather than assumed from the boxes ticked here.
      </p>

      {composeNote ? (
        <p className="rn-muted" role="status" data-compose-gate={composeGate.kind}>
          {composeNote}
        </p>
      ) : null}

      {/* ----------------------------------------------------------------- */}
      {/* The roles that exist, and the honest statement about their contents */}
      {/* ----------------------------------------------------------------- */}
      <h3>Roles this organization has composed</h3>
      {composedRoles.length === 0 ? (
        <SuccessEmpty
          heading="None composed"
          message="Every role in this organization is one the platform defines. That is a real answer — the server listed the roles and none of them was composed here."
        />
      ) : (
        <ul className="rn-list">
          {composedRoles.map((role) => (
            <li key={role.id} data-composed-role={role.key}>
              <strong>{role.name}</strong> <code>{role.key}</code>
              <span className="rn-muted">
                {' '}
                · {role.memberCount} active holder{role.memberCount === 1 ? '' : 's'}
              </span>
              {/*
                `permissionKeys` is deliberately NOT read here. It joins
                `role_permission`, which a composition never writes, so it is []
                for every composed role — the absence of a JOIN, not an absence
                of authority. The composed fields are the ones that answer.
              */}
              <CompositionReadback role={role} />
            </li>
          ))}
        </ul>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Compose                                                            */}
      {/* ----------------------------------------------------------------- */}
      <h3>Compose a new role</h3>
      <div className="rn-row">
        <label>
          Key
          <input
            type="text"
            value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
            placeholder="housing-lead"
          />
        </label>
        <label>
          Name
          <input
            type="text"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="Housing Lead"
          />
        </label>
      </div>

      <PermissionPicker
        groups={byModule}
        picked={picked}
        disabled={!mayCompose}
        onToggle={(key) =>
          setPicked((current) =>
            current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
          )
        }
        census={census}
      />

      <ConsequenceNote
        text={`This creates a role conferring ${picked.length} permission${picked.length === 1 ? '' : 's'}, organization-wide. It confers nothing to anybody until it is assigned to a person, and the server checks — again, at that moment — that you may confer every one of them. You cannot compose authority you do not hold yourself.`}
      />

      <div className="rn-row">
        <Button
          variant="primary"
          disabled={
            !mayCompose ||
            picked.length === 0 ||
            roleKey.trim() === '' ||
            roleName.trim() === '' ||
            compose.isPending
          }
          onClick={() => compose.mutate()}
        >
          {compose.isPending ? 'Composing…' : 'Compose this role'}
        </Button>
      </div>

      {/* --------------------------------------------------------------- */}
      {/* Recompose — a REPLACEMENT of a set nobody can currently read.    */}
      {/* --------------------------------------------------------------- */}
      {composedRoles.length === 0 ? null : (
        <div className="rn-col" data-recompose-panel="true">
          <h3>Or replace an existing role’s permissions</h3>
          <label>
            Role to replace
            <select value={replacing} onChange={(e) => setReplacing(e.target.value)}>
              <option value="">Choose a composed role…</option>
              {composedRoles.map((r) => (
                <option key={r.id} value={r.key}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <ConsequenceNote
            text={`This REPLACES that role's permission set with the ${picked.length} ticked above — it does not add to it. Keys you leave unticked are withdrawn from every current holder in the same transaction. And this product has no route that reads a composed role's permissions, so nothing here can show you what you are replacing: what disappears will be visible only in the authority history afterwards.`}
          />
          <Button
            variant="destructive"
            disabled={!mayCompose || replacing === '' || picked.length === 0 || recompose.isPending}
            onClick={() => recompose.mutate()}
          >
            {recompose.isPending ? 'Replacing…' : 'Replace this role’s permissions'}
          </Button>
        </div>
      )}

      {composeError ? (
        <p role="alert" data-compose-error="true">
          {composeError}
        </p>
      ) : null}

      {composed ? (
        <p role="status" data-composed-key={composed.key}>
          The server accepted <code>{composed.key}</code> with {composed.permissions.length}{' '}
          permission{composed.permissions.length === 1 ? '' : 's'}. That count is the request the
          server accepted, not a re-read of the stored role — the list above is refetched from{' '}
          <code>/roles/overview</code> and is what the server holds.
        </p>
      ) : null}

      <AssignComposedRole
        roles={composedRoles}
        gate={assignGate}
        onAssigned={() => void qc.invalidateQueries({ queryKey: ['roles', 'overview'] })}
      />
    </section>
  );
}

/**
 * WHAT A COMPOSED ROLE CONFERS — three answers, never collapsed to one.
 *
 * `composedPermissionKeys` is optional on the wire because a bundle can outlive
 * the server that answers it. `undefined` is "the server did not say", and it
 * is NOT `[]`: the first is unreadable, the second is a role that composes
 * nothing. `data-composition-readable` carries the difference for anything
 * reading this screen after the fact.
 *
 * The scope comes from `composedPermissions`, which is the same set in the
 * shape `PUT /orgchart/roles/:key` takes back — a key at program or location
 * scope confers less than the same key at org scope, and a readback that
 * dropped the scope would overstate the role.
 */
function CompositionReadback({ role }: { role: UsersNS.RoleOverview }) {
  const keys = role.composedPermissionKeys;
  if (keys === undefined || keys === null) {
    return (
      <p className="rn-muted" data-composition-readable="false">
        What this role confers cannot be read back: this server did not send the composition. Its{' '}
        {role.memberCount} holder{role.memberCount === 1 ? '' : 's'} do hold something — check any
        one of them under People, where their effective authority is resolved by the server.
      </p>
    );
  }
  if (keys.length === 0) {
    return (
      <p className="rn-muted" data-composition-readable="empty">
        This role confers nothing. That is the server’s answer, not a missing read: it lists the
        role and lists no permission on it.
      </p>
    );
  }
  const scopes = new Map((role.composedPermissions ?? []).map((p) => [p.key, p]));
  return (
    <p className="rn-muted" data-composition-readable="true">
      Confers{' '}
      {keys.map((key, i) => {
        const scope = scopes.get(key);
        const scoped = scope && scope.scopeType !== 'org';
        return (
          <span key={key}>
            {i > 0 ? ', ' : ''}
            <code>{key}</code>
            {scoped ? ` (${scope.scopeType} scope)` : ''}
          </span>
        );
      })}
      . A recomposition replaces this set.
    </p>
  );
}

/**
 * THE PICKER. Every key in the live catalog appears; only grantable ones can be
 * ticked; every unpickable one says why in the product's own words.
 */
function PermissionPicker({
  groups,
  picked,
  disabled,
  onToggle,
  census,
}: {
  groups: { module: string; entries: KeyStanding[] }[];
  picked: readonly string[];
  disabled: boolean;
  onToggle: (key: string) => void;
  census: { grantable: number; reserved: number; undescribed: number; unstated: number };
}) {
  const withheld = census.reserved + census.undescribed + census.unstated;
  return (
    <div className="rn-col" aria-label="Permissions">
      <p className="rn-muted" data-census-reserved={census.reserved}>
        {census.grantable} of {census.grantable + withheld} permissions in this organization’s
        catalog can be put in a role.
        {census.reserved > 0
          ? ` ${census.reserved} ${census.reserved === 1 ? 'is' : 'are'} reserved: real permissions the product declines to hand out to anybody, by any route. They are listed below rather than hidden, so a refusal about one does not look like a fault.`
          : ''}
        {census.undescribed + census.unstated > 0
          ? ` ${census.undescribed + census.unstated} cannot be offered because this build cannot say whether they may be granted.`
          : ''}
      </p>
      {groups.map((group) => (
        <fieldset key={group.module} className="rn-col">
          <legend>{group.module}</legend>
          <ul className="rn-list">
            {group.entries.map((entry) => {
              const badge = grantStandingBadge(entry.standing);
              const selectable = entry.standing === 'grantable';
              return (
                <li
                  key={entry.key}
                  data-permission-key={entry.key}
                  data-grant-standing={entry.standing}
                >
                  <label>
                    <input
                      type="checkbox"
                      checked={picked.includes(entry.key)}
                      /*
                        A reserved key's control is DISABLED, not absent. An
                        absent row makes the server's refusal look like a bug;
                        an enabled one invites an administrator to keep pressing
                        against a decision no grant can change.
                      */
                      disabled={disabled || !selectable}
                      onChange={() => onToggle(entry.key)}
                    />{' '}
                    {entry.name} <code>{entry.key}</code>
                  </label>
                  {badge ? (
                    <Pill variant={entry.standing === 'reserved' ? 'unsure' : 'plain'}>
                      {badge}
                    </Pill>
                  ) : null}
                  {entry.explanation ? (
                    <p className="rn-muted" data-standing-explanation={entry.standing}>
                      {entry.explanation}
                    </p>
                  ) : null}
                  {entry.warning ? (
                    <p className="rn-muted" data-permission-warning="true">
                      {entry.warning}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
    </div>
  );
}

/**
 * Assigning a composed role, and then ASKING THE SERVER what the person now
 * holds.
 *
 * The re-read is the whole point. `assignComposedRole` materialises the role's
 * entries into the holder's grants inside the same transaction, subject to the
 * ceiling — so what actually landed is a server fact, and the only honest way to
 * show it is to ask. A screen that echoed the role's tick list back would be
 * asserting an authority nothing confirmed.
 */
function AssignComposedRole({
  roles,
  gate,
  onAssigned,
}: {
  roles: UsersNS.RoleOverview[];
  gate: ReturnType<typeof authorityGateAll>;
  onAssigned: () => void;
}) {
  const permissions = usePermissions();
  const canReadDirectory = mayListWorkers(permissions);
  const [userId, setUserId] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reservedRefusal, setReservedRefusal] = useState(false);
  const [assignedTo, setAssignedTo] = useState<string | null>(null);

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-orgchart'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });
  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );
  const candidates = (workers.data ?? []).filter((w) => w.userId);

  const authority = useQuery({
    queryKey: ['orgchart', 'effective-authority', assignedTo ?? ''],
    queryFn: () => orgchart.effectiveAuthority(assignedTo!),
    enabled: assignedTo !== null,
    staleTime: 0,
    gcTime: 0,
  });

  const assign = useMutation({
    mutationFn: () => orgchart.assignComposedRole({ userId, roleKey }),
    onSuccess: () => {
      setAssignedTo(userId);
      setError(null);
      setReservedRefusal(false);
      onAssigned();
    },
    onError: (err: unknown) => {
      setError(writeRefusalMessage(err, 'We couldn’t assign that role.'));
      setReservedRefusal(refusedForReservedKey(err));
    },
  });

  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canReadDirectory;
  const picker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          candidates.length,
        ),
    'people',
    'Choose someone…',
  );

  const note = gateSentence(gate, 'assign a composed role');
  const mayAssign = gateOpen(gate);

  return (
    <div className="rn-col" data-assign-panel="composed-role">
      <h3>Give a composed role to somebody</h3>
      {note ? (
        <p className="rn-muted" role="status" data-assign-gate={gate.kind}>
          {note}
        </p>
      ) : null}

      <div className="rn-row">
        <label>
          Person
          <select
            value={userId}
            disabled={picker.disabled}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">{picker.placeholder}</option>
            {candidates.map((w) => (
              <option key={w.userId!} value={w.userId!}>
                {w.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Role
          <select value={roleKey} onChange={(e) => setRoleKey(e.target.value)}>
            <option value="">Choose a composed role…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.key}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {picker.note ? (
        <p className="rn-muted" role="status">
          {picker.note}
        </p>
      ) : null}

      <ConsequenceNote text="This puts the role's permissions onto this person's account immediately and permanently — unlike coverage, nothing expires. The server checks that you may confer every one of them, against what you yourself hold, at the moment you press. What they end up holding is read back from the server below." />

      <Button
        variant="primary"
        disabled={!mayAssign || userId === '' || roleKey === '' || assign.isPending}
        onClick={() => assign.mutate()}
      >
        {assign.isPending ? 'Assigning…' : 'Assign this role'}
      </Button>

      {error ? (
        <p role="alert" data-assign-error="true">
          {error}
          {reservedRefusal ? (
            <span data-reserved-refusal="true">
              {' '}
              A reserved permission is in this role. Nobody can be given it — not you, not a wider
              grant, not another administrator. Recompose the role without it.
            </span>
          ) : null}
        </p>
      ) : null}

      {assignedTo ? (
        <EffectiveAuthorityPanel query={authority} name={people.name(assignedTo)} />
      ) : null}
    </div>
  );
}

/**
 * WHAT THIS PERSON ACTUALLY HOLDS — the server's answer, at a stated instant.
 *
 * The four states are all distinct here, and the middle two are the ones that
 * matter: a failed read must not render as "holds nothing", and a refusal must
 * not render as a retry. A real empty set — the server resolved and returned no
 * grants — is the only branch entitled to say the person holds nothing.
 */
function EffectiveAuthorityPanel({
  query,
  name,
}: {
  query: {
    data: orgchart.EffectiveAuthorityView | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => void;
  };
  name: string;
}) {
  if (query.isLoading) return <SkeletonSet surface="drawer" rows={2} />;
  if (query.isError) {
    return (
      <LoadFailure
        error={query.error}
        message={`The role was assigned. We couldn’t then re-read what ${name} holds — do not read that as “nothing”, it is a failure to ask.`}
        onRetry={() => query.refetch()}
      />
    );
  }
  const authority = query.data;
  if (!authority) return null;

  const scoped = authority.own.filter((g) => g.scopeType !== 'org');
  return (
    <div className="rn-col" role="status" data-effective-own={authority.own.length}>
      <h4>What {name} holds now, according to the server</h4>
      {authority.own.length === 0 ? (
        <SuccessEmpty
          heading="No grants"
          message={`The server resolved ${name}'s authority and returned an empty set. Assigning the role conferred nothing that survived the ceiling.`}
        />
      ) : (
        <>
          <p>
            {authority.own.length} permission grant{authority.own.length === 1 ? '' : 's'}
            {scoped.length > 0
              ? `, ${scoped.length} of which ${scoped.length === 1 ? 'is' : 'are'} confined to a program or location rather than organization-wide`
              : ', all organization-wide'}
            . Resolved at {new Date(authority.at).toLocaleTimeString()}.
          </p>
          <ul className="rn-list">
            {authority.own.map((grant) => (
              <li key={`${grant.key}-${grant.scopeType}-${grant.scopeId ?? 'org'}`}>
                <code>{grant.key}</code>
                <span className="rn-muted">
                  {' '}
                  · {grant.scopeType === 'org' ? 'organization-wide' : `${grant.scopeType} scoped`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {authority.delegated.length > 0 ? (
        <p className="rn-muted" data-effective-delegated={authority.delegated.length}>
          {name} is also borrowing authority from {authority.delegated.length} coverage window
          {authority.delegated.length === 1 ? '' : 's'} right now. That part is temporary and is not
          what this role conferred.
        </p>
      ) : null}
    </div>
  );
}

function groupStandings(
  standings: readonly KeyStanding[],
  modules: readonly string[],
): { module: string; entries: KeyStanding[] }[] {
  const byModule = new Map<string, KeyStanding[]>();
  for (const entry of standings) {
    const list = byModule.get(entry.module) ?? [];
    list.push(entry);
    byModule.set(entry.module, list);
  }
  // The server's own module order, then 'Other' for keys with no metadata. A
  // key nothing describes still gets a row; it is never dropped.
  return [...modules, 'Other']
    .filter((m) => byModule.has(m))
    .map((m) => ({
      module: m,
      entries: byModule.get(m)!.sort((a, b) => a.name.localeCompare(b.name)),
    }));
}
